import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useDeleteResource, useUpdateResource } from '@app/src/api/updateHooks'
import type { ActionButton } from '@app/src/components/ActionButtons'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import type RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { useWriteAccess } from '@app/src/hooks/useWriteAccess'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import {
  Operators,
  RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
  RESPONSE_LATEST_SUBMITTED_FILTER,
} from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { typography } from '@app/src/theme/typography'
import type { FileResponse } from '@app/src/types/api'
import type { ExportColumn } from '@app/src/types/export'
import type { RawExportPayload } from '@app/src/types/filter'
import type {
  ExcelExportColumnsJson,
  ExcelExportSettings,
  ExcelExportSettingsPayload,
} from '@app/src/types/resourceExplorer'
import type { Schema } from '@app/src/types/schemas'
import { insertIf } from '@app/src/utils/helpersTs'
import { br } from '@app/src/utils/translationMarkup'
import { AmplitudeTrackingEvents, NotificationSeverity, ResourceTypes } from '@app/src/wf-constants'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import EditIcon from '@mui/icons-material/Edit'
import UndoIcon from '@mui/icons-material/Undo'
import type { RadioGroupProps } from '@mui/material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDrawer } from '../DrawerContext'
import DrawerView from '../DrawerView'
import ExportColumnItem from './ExportColumnItem'

export const nonLetterNumberOrUnderScoreRegex = /\W/g

export const cleanColumnPath = (path: string) => path.replace(nonLetterNumberOrUnderScoreRegex, '_')

enum DataExportOptions {
  FILTERED_DATA = 'filtered',
  RAW_DATA = 'raw',
  SELECTED_DATA = 'selected',
}

interface DrawerViewExportProps {
  resourceType: (typeof ResourceTypes)[keyof typeof ResourceTypes]
  count: number
  rawExportPayload: RawExportPayload
  userFilter: FilterGroup[]
  exportColumns?: ExportColumn[]
  // Export paths of the columns that start checked. When omitted every column starts checked (legacy
  // default). Callers that mirror a table view pass the visible columns here; the rest show unchecked.
  defaultCheckedColumns?: string[]
  selectedIds?: number[]
  selectedFilterField?: string // Optional custom field name for filtering (defaults to 'id')
  onlyLatest?: boolean
  resourceView?: string
  disableExportSettings?: boolean
  // Drives the checked state from the caller's `defaultCheckedColumns` (a table view) and neither loads
  // nor writes the saved export template, so the selection can't drift from what the table shows.
  syncColumnsToView?: boolean
  // Names the exported file in place of the resource name, for a view that exports one resource in a
  // shape of its own. Omit it to keep the resource name.
  exportName?: string
  // Names what is being exported, under the drawer title. Defaults to the resource name, which reads
  // oddly for a view that exports one resource in a shape of its own.
  subTitle?: string
}

/**
 * A configured column set deviates from its default when a column is added/removed/reordered, its
 * checked state changed, or it was renamed. Only a real deviation gets persisted as an export
 * template — so a default that merely mirrors the current table view is not saved on its own.
 */
export const hasColumnDeviation = (configured: ExcelExportColumnsJson[], baseline: ExcelExportColumnsJson[]): boolean =>
  configured.length !== baseline.length ||
  configured.some(
    (column, index) =>
      column.path !== baseline[index]?.path ||
      column.isChecked !== baseline[index]?.isChecked ||
      column.displayName !== baseline[index]?.displayName,
  )

export const getExportColumns = (schema: Schema) =>
  schema?.items
    .filter(schema => schema.display?.export && schema.exportPath)
    .map(schema => ({ translationId: schema.translationId, exportPath: schema.exportPath ?? '' }))

const DrawerViewExport: React.FC<DrawerViewExportProps> = ({
  resourceType,
  count,
  rawExportPayload,
  userFilter,
  exportColumns,
  defaultCheckedColumns,
  selectedIds = [],
  selectedFilterField = 'id',
  onlyLatest,
  resourceView = resourceType,
  disableExportSettings = false,
  syncColumnsToView = false,
  exportName,
  subTitle,
}) => {
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { formatMessage } = useIntl()
  const { post } = useApi()
  const { closeDrawer } = useDrawer()
  const [dataExportOption, setDataExportOption] = useState<DataExportOptions>(
    selectedIds.length > 0 ? DataExportOptions.SELECTED_DATA : DataExportOptions.RAW_DATA,
  )
  const [isLoading, setLoading] = useState(false)
  const [isEditingColumnNames, setIsEditingColumnNames] = useState(false)
  const queryClient = useQueryClient()
  const formMethods = useForm()
  const { trackEvent } = useAmplitude()
  const hasWriteAccess = useWriteAccess()

  const { data: excelExportSettings, isLoading: isExcelExportSettingsLoading } = useFetchResource<ExcelExportSettings>({
    key: [FetchKey.ExcelExportSettings, resourceType, resourceView],
    endpoint: endpoints.excelExportSettings(resourceType, resourceView),
    options: {
      enabled: Boolean(resourceType && resourceView && !disableExportSettings && !syncColumnsToView),
    },
  })

  const { mutateAsync: createExcelExportSettings } = useUpdateResource<
    ExcelExportSettings,
    ExcelExportSettingsPayload
  >()

  const { mutateAsync: mutateDeleteExcelExportSettings } = useDeleteResource()

  const exportColumnsResolved = useMemo(
    (): ExcelExportColumnsJson[] =>
      exportColumns?.map(column => ({
        // The table-synced drawer prefers an explicit displayName (dynamic custom-category names);
        // other callers keep resolving from the translation key, so their behaviour is unchanged.
        displayName:
          syncColumnsToView && column.displayName
            ? column.displayName
            : formatMessage({ id: column.translationId }, column.translationValues),
        translationId: column.translationId,
        path: column.exportPath,
        isChecked: defaultCheckedColumns ? defaultCheckedColumns.includes(column.exportPath) : true,
      })) ?? [],
    [exportColumns, defaultCheckedColumns, syncColumnsToView],
  )

  const amplitudeBaseEventProps = useMemo(
    () => ({
      resource_type: resourceType,
      resource_view: resourceView,
    }),
    [resourceType, resourceView],
  )

  const [configuredColumns, setConfiguredColumns] = useState<ExcelExportColumnsJson[]>(exportColumnsResolved ?? [])

  const { handleSubmit, reset } = formMethods

  useEffect(() => {
    if (excelExportSettings?.columnsJson?.length) {
      setConfiguredColumns(excelExportSettings.columnsJson)
    } else {
      setConfiguredColumns(exportColumnsResolved ?? [])
    }
  }, [excelExportSettings?.columnsJson, exportColumnsResolved])

  useEffect(() => {
    const formValues = configuredColumns.reduce((acc, column) => {
      const fieldName = cleanColumnPath(column.path)
      return {
        ...acc,
        [fieldName]: column.displayName,
      }
    }, {})
    reset(formValues)
  }, [configuredColumns, reset])

  useEffect(() => {
    trackEvent({
      name: AmplitudeTrackingEvents.Accessor.ExcelExport.Opened,
      eventProps: amplitudeBaseEventProps,
    })
  }, [])

  const handleToggleColumn = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, path: string) => {
      const updatedColumns = configuredColumns?.map(column =>
        column.path === path ? { ...column, isChecked: event.target.checked } : column,
      )
      setConfiguredColumns(updatedColumns)
    },
    [configuredColumns],
  )

  const handleToggleAllColumns = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedColumns = configuredColumns?.map(column => ({ ...column, isChecked: event.target.checked }))
      setConfiguredColumns(updatedColumns)
    },
    [configuredColumns],
  )

  const handleRadioChange: RadioGroupProps['onChange'] = event => {
    setDataExportOption(event.target.value as DataExportOptions)
  }

  const handleResetOrderAndNameClick = useCallback(() => {
    const updatedColumns = [
      ...configuredColumns
        .sort((a, b) => {
          return (
            exportColumnsResolved.findIndex(x => x.path === a.path) -
            exportColumnsResolved.findIndex(x => x.path === b.path)
          )
        })
        .map((column, index) => ({
          ...column,
          displayName: exportColumnsResolved[index].displayName,
        })),
    ]
    setConfiguredColumns(updatedColumns)
  }, [configuredColumns, exportColumnsResolved])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return

    if (active.id !== over.id) {
      setConfiguredColumns(columns => {
        const oldIndex = columns.findIndex(x => x.path === active.id)
        const newIndex = columns.findIndex(x => x.path === over.id)
        return arrayMove(columns, oldIndex, newIndex)
      })
    }
  }, [])

  const onSaveColumnNames = (data: Record<string, string>) => {
    const updatedColumns = configuredColumns.map(column => {
      const fieldName = cleanColumnPath(column.path)
      return {
        path: column.path,
        displayName: data[fieldName],
        isChecked: column.isChecked,
      }
    })
    trackEvent({
      name: AmplitudeTrackingEvents.Accessor.ExcelExport.ChangedColumnNames,
      eventProps: amplitudeBaseEventProps,
    })
    setConfiguredColumns(updatedColumns)
    setIsEditingColumnNames(false)
  }

  const areColumnsReorderedOrRenamed = useMemo(
    () =>
      configuredColumns?.some(
        (column, index) =>
          column.path !== exportColumnsResolved[index]?.path ||
          column.displayName !== exportColumnsResolved[index]?.displayName,
      ),
    [configuredColumns, exportColumnsResolved],
  )
  const areAllColumnsChecked = useMemo(() => configuredColumns?.every(column => column.isChecked), [configuredColumns])
  const areSomeColumnsChecked = useMemo(
    () => configuredColumns?.some(column => column.isChecked) && !areAllColumnsChecked,
    [configuredColumns, areAllColumnsChecked],
  )

  const getOnlyLatestFilter = () => {
    if (!onlyLatest) return []

    if (resourceType === ResourceTypes.Response) return [RESPONSE_LATEST_SUBMITTED_FILTER]
    if ([ResourceTypes.ResponseItem, ResourceTypes.Answer].includes(resourceType))
      return [RESPONSE_ITEM_LATEST_SUBMITTED_FILTER]

    return []
  }

  const getExportObject = () => {
    const onlyLatestFilter = getOnlyLatestFilter()
    const onlyIncludedColumns = configuredColumns
      .filter(x => x.isChecked)
      .map(x => ({ displayName: x.displayName, path: x.path }))

    if (dataExportOption === DataExportOptions.RAW_DATA) {
      return {
        ...rawExportPayload,
        filter: [...(rawExportPayload?.filter ? rawExportPayload.filter : []), ...onlyLatestFilter],
        export: onlyIncludedColumns,
        IsStandardExport: true,
        ExportName: exportName,
      }
    }
    if (dataExportOption === DataExportOptions.FILTERED_DATA) {
      return {
        ...rawExportPayload,
        filter: [...(rawExportPayload?.filter ? rawExportPayload.filter : []), ...userFilter, ...onlyLatestFilter],
        export: onlyIncludedColumns,
        IsStandardExport: true,
        ExportName: exportName,
      }
    }
    if (dataExportOption === DataExportOptions.SELECTED_DATA) {
      return {
        ...rawExportPayload,
        filter: [
          ...(rawExportPayload?.filter ? rawExportPayload.filter : []),
          ...[{ name: selectedFilterField, filters: [{ operator: Operators.In, value: selectedIds }] }],
          ...onlyLatestFilter,
        ],
        export: onlyIncludedColumns,
        IsStandardExport: true,
        ExportName: exportName,
      }
    }
    return {}
  }

  const isModifiedFromDefault = useMemo(
    () => hasColumnDeviation(configuredColumns, exportColumnsResolved),
    [configuredColumns, exportColumnsResolved],
  )

  const handleExportSettingsUpdate = async () => {
    if (syncColumnsToView) return

    if (!isModifiedFromDefault && excelExportSettings) {
      await mutateDeleteExcelExportSettings({
        url: endpoints.deleteExcelExportSettings(excelExportSettings.id),
      })
      trackEvent({
        name: AmplitudeTrackingEvents.Accessor.ExcelExport.DeletedExportSettings,
        eventProps: amplitudeBaseEventProps,
      })
      queryClient.invalidateQueries({ queryKey: [FetchKey.ExcelExportSettings, resourceType, resourceView] })
    }
    if (isModifiedFromDefault) {
      const excelExportSettingsObject: ExcelExportSettingsPayload = {
        resourceType,
        resourceView,
        columnsJson: configuredColumns,
      }
      await createExcelExportSettings({ url: endpoints.saveExcelExportSettings, body: excelExportSettingsObject })
      trackEvent({
        name: AmplitudeTrackingEvents.Accessor.ExcelExport.SavedExportSettings,
        eventProps: amplitudeBaseEventProps,
      })
    }
  }

  const handleExportRequest = async () => {
    const exportObject = getExportObject()
    const res = await post<FileResponse, { [key: string]: unknown }>({
      url: endpoints.queueDataExport(resourceType),
      body: exportObject,
    })
    trackEvent({
      name: AmplitudeTrackingEvents.Accessor.ExcelExport.ExportRequested,
      eventProps: {
        ...amplitudeBaseEventProps,
        data_export_option: dataExportOption,
        columns_reordered_or_renamed: areColumnsReorderedOrRenamed,
        all_columns_checked: areAllColumnsChecked,
        some_columns_checked: areSomeColumnsChecked,
      },
    })
    if (res) {
      showSnackbar({
        message: formatMessage({ id: 'notifications.successfulExportQueueRequest' }, { br }),
        severity: NotificationSeverity.success,
      })
    } else {
      showSnackbar({
        message: formatMessage({ id: 'notifications.error' }),
        severity: NotificationSeverity.error,
      })
    }
  }

  const handleSubmitExport = async () => {
    setLoading(true)
    try {
      await Promise.all([handleExportSettingsUpdate(), handleExportRequest()])
    } catch (e) {
      const typedError = e as { requestError: RequestError }
      showErrorNotification({ requestError: typedError.requestError })
    } finally {
      setLoading(false)
      closeDrawer()
    }
  }

  const buttons: ActionButton[] = isEditingColumnNames
    ? [
        {
          label: formatMessage({ id: 'export.doneEditColumnNames' }),
          variant: 'contained',
          type: 'submit',
        },
      ]
    : [
        ...insertIf<ActionButton>(!disableExportSettings && hasWriteAccess, {
          label: formatMessage({ id: 'export.editColumnNames' }),
          startIcon: <EditIcon />,
          variant: 'outlined',
          onClick: () => setIsEditingColumnNames(prev => !prev),
        }),
        {
          label: formatMessage({ id: 'export.export' }),
          variant: 'contained',
          onClick: handleSubmitExport,
          disabled: !dataExportOption || (!areSomeColumnsChecked && !areAllColumnsChecked),
          loading: isLoading,
        },
      ]

  return (
    <FormProvider {...formMethods}>
      <DrawerView
        title={formatMessage({ id: 'export.title' })}
        subTitle={subTitle ?? formatMessage({ id: `resourceTypes.${resourceType}` })}
        stackButtons
        buttons={buttons}
        onFormSubmit={handleSubmit(onSaveColumnNames)}
      >
        {isExcelExportSettingsLoading ? (
          <Box height="100%" display="flex" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box px={3}>
              <FormControl variant="outlined">
                <FormLabel component="legend">{formatMessage({ id: 'export.description' })}</FormLabel>
                <RadioGroup value={dataExportOption} onChange={handleRadioChange}>
                  <FormControlLabel
                    value={DataExportOptions.RAW_DATA}
                    control={<Radio />}
                    label={formatMessage({ id: 'export.rawOption' })}
                  />
                  {Boolean(userFilter?.length) && Boolean(count) && (
                    <FormControlLabel
                      value={DataExportOptions.FILTERED_DATA}
                      control={<Radio />}
                      label={formatMessage({ id: 'export.filteredOption' }, { rows: count })}
                    />
                  )}
                  {Boolean(selectedIds?.length) && (
                    <FormControlLabel
                      value={DataExportOptions.SELECTED_DATA}
                      control={<Radio />}
                      label={formatMessage({ id: 'export.selectedOption' }, { rows: selectedIds.length })}
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </Box>
            {!disableExportSettings && exportColumns?.length && hasWriteAccess && (
              <Box pt={2}>
                <Box pl={3} pr={2} display="flex" sx={{ alignItems: 'center' }}>
                  <Box py={1.5}>
                    <FormLabel component="legend">{formatMessage({ id: 'export.columnSettingsHeader' })}</FormLabel>
                  </Box>
                  {areColumnsReorderedOrRenamed && (
                    <Tooltip arrow placement="top" title={formatMessage({ id: 'export.resetColumnsTooltip' })}>
                      <Button size="small" onClick={handleResetOrderAndNameClick} sx={{ marginLeft: 'auto' }}>
                        <UndoIcon sx={{ transform: 'scale(0.8)', transformOrigin: 'center', marginRight: '3px' }} />
                        {formatMessage({ id: 'general.reset' })}
                      </Button>
                    </Tooltip>
                  )}
                </Box>

                <Stack sx={{ backgroundColor: 'grey.100' }} px={2} py={1} direction="row" alignItems="center">
                  {!isEditingColumnNames && (
                    <Tooltip title={formatMessage({ id: 'export.toggleAllColumns' })} arrow placement="left">
                      <Checkbox
                        sx={{ marginLeft: '-3px' }}
                        onChange={handleToggleAllColumns}
                        checked={areAllColumnsChecked}
                        indeterminate={areSomeColumnsChecked}
                      />
                    </Tooltip>
                  )}
                  <Typography fontWeight={typography.fontWeightMedium} sx={{ marginLeft: 1 }}>
                    {formatMessage({ id: 'export.column' })}
                  </Typography>
                </Stack>
                <DndContext onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={configuredColumns.map(column => column.path)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Stack sx={{ backgroundColor: 'grey.50' }}>
                      {configuredColumns?.map(column => (
                        <ExportColumnItem
                          key={column.path}
                          column={column}
                          isEditingName={isEditingColumnNames}
                          onToggleColumn={handleToggleColumn}
                          originalColumnName={
                            exportColumnsResolved.find(x => x.path === column.path)?.displayName ?? ''
                          }
                        />
                      ))}
                    </Stack>
                  </SortableContext>
                </DndContext>
              </Box>
            )}
          </>
        )}
      </DrawerView>
    </FormProvider>
  )
}

export default DrawerViewExport
